import * as React from 'react';
import { ProductSizeInterface } from '../../../../../Interface/ProductSize';
import { formatCurrencyEur } from '../../../../Helper/formatter';
import { getProductSizeDiscountDisplay } from '../../../../Helper/flavourDiscount';
import styles from './ProductSizePrice.module.scss?module';

/**
 * Niedrigster effektiver Clearance-Preis über ALLE Flavours der PID. Gibt es
 * unterschiedliche Preise zwischen den Flavours, wird "From" vor den Preis
 * gesetzt. Für Gäste liegt kein combined_price vor -> null. (P-4877, Punkt 2)
 */
function useClearancePriceInfo(product_size: ProductSizeInterface) {
  return React.useMemo(() => {
    const attrs = product_size.product_attributes ?? [];
    const hasClearance = attrs.some(
      (a) => (a.clearance_discount_percent ?? 0) > 0
    );
    if (!hasClearance) return null;

    const prices = attrs
      .filter((a) => (a.clearance_discount_percent ?? 0) > 0)
      .map((a) => a.combined_price)
      .filter((p): p is number => p != null);
    if (prices.length === 0) return null;

    const minPrice = Math.min(...prices);
    const hasVariedPrices = new Set(prices).size > 1;

    return { minPrice, hasVariedPrices };
  }, [product_size.product_attributes]);
}

export default function ProductSizePrice({
  product_size,
  inline,
}: {
  product_size: ProductSizeInterface;
  inline?: boolean;
}) {
  const clearancePriceInfo = useClearancePriceInfo(product_size);
  // Der Clearance-Aggregatpreis ("From"/Min) gilt nur, wenn das Produkt über die
  // zentrale Entscheidung als Clearance kommuniziert wird. Bei Promotion (Discount
  // oder Plus) gilt der normale Produktpreis (P-4877).
  const display = React.useMemo(
    () => getProductSizeDiscountDisplay(product_size),
    [product_size]
  );
  const clearance = display.useClearancePrice ? clearancePriceInfo : null;

  if (inline) {
    return (
      <div className="d-flex align-items-center justify-content-end">
        {product_size.price_show_regular && (
          <small className="fst-italic text-decoration-line-through me-3">
            {formatCurrencyEur(product_size.price_regular)}
          </small>
        )}
        <div
          className={[
            'fw-bold fst-italic',
            product_size.price_is_red ? 'text-danger' : '',
          ].join(' ')}
        >
          {formatCurrencyEur(product_size.current_price)}
        </div>
      </div>
    );
  }

  const displayPrice = clearance ? clearance.minPrice : product_size.price;
  const showStrikethrough = product_size.price_show_regular || !!clearance;
  const isPriceRed = product_size.price_is_red || !!clearance;

  return (
    <div className="d-flex flex-column align-items-end justify-content-end">
      {showStrikethrough && (
        <small className="fst-italic text-decoration-line-through">
          {formatCurrencyEur(product_size.price_regular)}
        </small>
      )}
      {clearance?.hasVariedPrices ? (
        <div className={styles.fromLabel}>From</div>
      ) : null}
      <div
        className={['type-price', isPriceRed ? 'text-danger' : ''].join(' ')}
      >
        {formatCurrencyEur(displayPrice)}
      </div>
    </div>
  );
}
